import katex from "katex";

/**
 * Renders the LaTeX the models emit into markup the previews can display.
 *
 * Generated physics and maths content is full of it — a survey of the stored
 * step outputs and approved content found 3,365 math spans using \frac, \vec,
 * \sqrt, \begin{}…\end{} and stacked subscripts. None of it rendered: the
 * previews showed `\(C_1 = \frac{Q}{V}\)` as literal text.
 *
 * MathML output, not KaTeX's default HTML. The default emits ~3.7KB of nested
 * spans per formula that position glyphs by hand and depend on 60 web-font
 * files; the previews render inside `sandbox=""` iframes with no network, so
 * every one of those font requests fails and the formula collapses into
 * overlapping characters. MathML is ~340 bytes, needs no fonts and no
 * stylesheet, and browsers lay it out natively.
 */

/** Delimiters, longest-first so `\[` is not mistaken for an escaped bracket.
 *  `$$…$$` is included for completeness though the current corpus has none. */
const DISPLAY_PATTERNS: RegExp[] = [/\\\[([\s\S]+?)\\\]/g, /\$\$([\s\S]+?)\$\$/g];
const INLINE_PATTERNS: RegExp[] = [/\\\(([\s\S]+?)\\\)/g];

/**
 * Undoes HTML entity encoding inside a formula before KaTeX sees it.
 *
 * The math spans are pulled out of an HTML *string*, so nothing has parsed the
 * markup and decoded its entities. A model writing a table writes `&` as the
 * column separator and escapes it to `&amp;` — correct HTML, but KaTeX is a
 * TeX parser, not an HTML one, so it reads the literal characters and renders
 * `amp;` into the output. `a &amp; b` displays as "aamp;b".
 *
 * Only the five characters that are actually meaningful in TeX and legal to
 * escape in HTML: `&` separates columns, `<` and `>` are relations, and the
 * quote forms appear inside \text{}. A general entity decoder would also touch
 * sequences that TeX gives its own meaning to.
 */
function decodeEntities(tex: string): string {
  return tex
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/g, "'")
    // Last: an input of `&amp;lt;` means a literal "&lt;", and decoding the
    // ampersand first would wrongly turn it into "<".
    .replace(/&amp;/g, "&");
}

function render(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(decodeEntities(tex), {
      displayMode,
      output: "mathml",
      // A malformed formula must not blank the preview: KaTeX renders what it
      // can and marks the rest, which is more useful to a reviewer than an
      // exception that takes the whole document down.
      throwOnError: false,
      strict: false,
    });
  } catch {
    // Belt and braces — throwOnError:false already covers parse failures, but
    // a renderer crash must still leave the surrounding content readable.
    return tex;
  }
}

/**
 * Replaces every math span in an HTML string with rendered MathML.
 *
 * Operates on the HTML the model produced, so formulas inside <p> and <li>
 * are converted in place while the surrounding markup is untouched.
 */
export function renderMathInHtml(html: string): string {
  // Preserve <script> and <style> tags so math patterns do not corrupt JS code or CSS.
  const preserved: string[] = [];
  const protectedHtml = html.replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, (match) => {
    const idx = preserved.length;
    preserved.push(match);
    return `___PRESERVED_BLOCK_${idx}___`;
  });

  let out = protectedHtml;

  // Display first: an inline pattern could otherwise match inside a display
  // block and split a formula in half.
  for (const pattern of DISPLAY_PATTERNS) {
    out = out.replace(pattern, (_match, tex: string) => render(tex.trim(), true));
  }
  for (const pattern of INLINE_PATTERNS) {
    out = out.replace(pattern, (_match, tex: string) => render(tex.trim(), false));
  }

  // Restore preserved blocks
  return out.replace(/___PRESERVED_BLOCK_(\d+)___/g, (_match, idx) => preserved[Number(idx)] ?? _match);
}

/** Whether a string contains anything worth running the renderer over. */
export function hasMath(text: string): boolean {
  return /\\\(|\\\[|\$\$/.test(text);
}
